// Request or route the company on the page (W8, priority 1).
//
// A strategist's click is a **request**: it queues for an admin or leadership and moves nothing,
// so the copy says "Requested", never "claimed". An admin sees the routing choice: request it
// for someone, or route it to a listed strategist now (the board's Route).

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchStrategists, panelMessage, postExtensionClaim, postExtensionRoute } from '../api/client';
import type { Viewer, WriteActionResult } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { writeOutcomeLine, writeOutcomeTone } from '../lib/writeOutcome';
import {
  MAX_REQUEST_REASON_LENGTH, REQUEST_REASON_LABEL, REQUEST_REASON_PLACEHOLDER, requestReason,
} from '../book/requestReason';

export function RequestRoute({ viewer, domain, name, accountId, onMyBook, excludedWhy }: {
  viewer: Viewer;
  domain: string;
  name: string | null;
  accountId: string | null;
  onMyBook: boolean;
  excludedWhy: string | null;
}) {
  const { getToken } = useAuthContext();
  const isAdmin = viewer.role === 'admin';
  const mayRequest = viewer.role === 'admin' || viewer.role === 'strategist';
  const strategistsQuery = useQuery({
    queryKey: ['strategists'], queryFn: () => fetchStrategists(getToken), enabled: isAdmin,
  });
  const [owner, setOwner] = useState('');
  // "Why this account?": a request is not sent without it (the server refuses one with a 400).
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState('');
  const reason = requestReason(why);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ line: string; tone: string } | null>(null);

  if (!mayRequest) return null;
  if (onMyBook) return <span className="extchip ok">On your book</span>;
  if (excludedWhy) return <span className="extchip bad" title={`Excluded: ${excludedWhy}. Nothing to request.`}>Excluded</span>;

  async function run(kind: 'request' | 'route') {
    setBusy(true);
    setResult(null);
    try {
      let reply: WriteActionResult;
      if (kind === 'route') {
        reply = await postExtensionRoute(getToken, { domain, owner, account_id: accountId ?? undefined });
      } else {
        if (!reason) return;
        reply = await postExtensionClaim(getToken, {
          domain, name: name ?? undefined, owner: owner || undefined, account_id: accountId ?? undefined, reason,
        });
        setWhy('');
        setAsking(false);
      }
      setResult({ line: writeOutcomeLine(reply, kind === 'route' ? 'Routed' : 'Requested'), tone: writeOutcomeTone(reply) });
    } catch (caught) {
      setResult({ line: panelMessage(caught, kind === 'route' ? 'Route failed.' : 'Request failed.'), tone: 'bad' });
    } finally {
      setBusy(false);
    }
  }

  const strategists = strategistsQuery.data?.strategists ?? [];
  const ownerName = strategists.find(person => person.email === owner)?.name;
  return (
    <div className="extrequest">
      {asking ? (
        <input
          type="text"
          className="extreason"
          aria-label={REQUEST_REASON_LABEL}
          placeholder={REQUEST_REASON_LABEL}
          title={REQUEST_REASON_PLACEHOLDER}
          maxLength={MAX_REQUEST_REASON_LENGTH}
          value={why}
          disabled={busy}
          autoFocus
          onChange={event => setWhy(event.target.value)}
          onKeyDown={event => { if (event.key === 'Enter' && reason) void run('request'); }}
        />
      ) : null}
      <div className="extactions">
        {isAdmin ? (
          <select className="extselect" value={owner} onChange={event => setOwner(event.target.value)} disabled={busy}
            aria-label="Request or route for">
            <option value="">For me</option>
            {strategists.map(person => <option key={person.email} value={person.email}>{person.name}</option>)}
          </select>
        ) : null}
        {/* Request asks "Why this account?" first; Send goes once there is a reason. */}
        <button type="button" className="btn primary" disabled={busy || (asking && !reason)}
          onClick={() => { if (asking) void run('request'); else setAsking(true); }}
          title={asking && !reason ? `${REQUEST_REASON_LABEL} is required.` : isAdmin ? undefined : 'A request waits for an admin or leadership to approve it.'}>
          {busy ? 'Sending…' : asking ? 'Send request' : owner && ownerName ? `Request for ${ownerName}` : 'Request'}
        </button>
        {asking ? (
          <button type="button" className="btn" disabled={busy} onClick={() => { setAsking(false); setWhy(''); }}>
            Cancel
          </button>
        ) : null}
        {isAdmin ? (
          <button type="button" className="btn" disabled={busy || !owner} onClick={() => { void run('route'); }}
            title={owner ? `Route to ${ownerName} now` : 'Pick a strategist to route to'}>
            Route
          </button>
        ) : null}
      </div>
      {result ? <p className={'extnote ' + result.tone} role="status">{result.line}</p> : null}
    </div>
  );
}
